import { Product } from '@danone-global/ct/interfaces'
import {
  FieldsCheckbox,
  FieldsInput,
  FieldsRating,
  FieldsSelect,
  validations,
} from '@danone-global/internal/react/components/fields'
import FormattedHtmlMessage from '@danone-global/internal/react/components/formatted-html-message'
import { ArrowLeftIcon } from '@danone-global/internal/react/components/icons'
import {
  Button,
  CtImage,
  Hr,
  Typography,
} from '@danone-global/internal/react/components/ui'
import { Theme, useCore } from '@danone-global/internal/react/core'
import { useProductUrl } from '@danone-global/internal/react/modules/product'
import { makeStyles } from '@material-ui/styles'
import React from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { useTranslatedAges } from './create-review.hooks'

const COMMENT_MAX_LENGTH = 2000

export interface Props {
  onSubmit: (values: any) => void
  product: Product
  creating: boolean
  productName?: string
  showBackButton?: boolean
  toggleCreateNew: () => void
  starClasses?: string
  starActiveClasses?: string
}

export const CreateReview: React.FC<Props> = ({
  product,
  onSubmit,
  creating,
  productName,
  showBackButton = true,
  toggleCreateNew,
  starClasses = '',
  starActiveClasses = '',
}) => {
  const classes = useStyles()
  const intl = useIntl()
  const ages = useTranslatedAges()
  const { features, config, localeConfig } = useCore()

  const { control, handleSubmit } = useForm({
    mode: 'all',
  })

  const productUrl = useProductUrl(
    product?.masterData?.current?.masterVariant?.attributesRaw || [],
  )

  return (
    <>
      {showBackButton && (
        <Button
          className={classes.backButton}
          onClick={toggleCreateNew}
          variant="flat"
        >
          <ArrowLeftIcon height={14} />

          <FormattedMessage
            defaultMessage="Back"
            id="btn.reviews.back-to-list"
          />
        </Button>
      )}

      <form className={classes.root} onSubmit={handleSubmit(onSubmit)}>
        <Typography className={classes.title} variant="h2">
          <FormattedMessage
            defaultMessage="Write a review"
            id="widgets.reviews.add-review.header"
          />
        </Typography>

        <Typography className={classes.subtitle}>
          <FormattedMessage
            defaultMessage="Help others by sharing your experience."
            id="widgets.reviews.add-review.subtitle"
          />
        </Typography>

        <div className={classes.productWrap}>
          {product.masterData.current?.masterVariant?.images?.length > 0 ? (
            <div className={classes.productImage}>
              <CtImage
                {...product.masterData.current.masterVariant.images[0]}
              />
            </div>
          ) : undefined}

          <div>
            <Typography className={classes.productName} variant="h5">
              {productName}
            </Typography>

            {productUrl ? (
              <Typography
                className={classes.productLink}
                component="a"
                href={productUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <FormattedMessage
                  defaultMessage="View product"
                  id="widgets.reviews.add-review.view-product"
                />
              </Typography>
            ) : undefined}
          </div>
        </div>

        <Typography variant="h3">
          <FormattedMessage
            defaultMessage="Your rating"
            id="widgets.reviews.add-review.your-rating"
          />
        </Typography>

        <FieldsRating
          control={control}
          disabled={creating}
          label={intl.formatMessage({
            id: 'widgets.reviews.add-review.rating',
            defaultMessage: 'Amount of stars',
          })}
          name="rating"
          starActiveClasses={starActiveClasses}
          starClasses={starClasses}
          required
        />

        <FieldsInput
          control={control}
          disabled={creating}
          label={intl.formatMessage({
            id: 'widgets.reviews.add-review.title',
            defaultMessage: 'Title',
          })}
          maxLength={55}
          name="title"
          required
        />

        <FieldsInput
          className={classes.commentField}
          control={control}
          disabled={creating}
          label={intl.formatMessage({
            id: 'widgets.reviews.add-review.comment',
            defaultMessage: 'Comment',
          })}
          maxLength={COMMENT_MAX_LENGTH}
          minLength={5}
          name="comment"
          placeholder={intl.formatMessage({
            id: 'widgets.reviews.add-review.comment.placeholder',
            defaultMessage:
              'Please do not enter any personal data, for example your address, in your review.',
          })}
          required={true}
          rows={6}
          showCounter={true}
        />

        {features?.disableReviewsAgeQuestion ? undefined : (
          <FieldsSelect
            control={control}
            disabled={creating}
            label={intl.formatMessage({
              id: 'widgets.reviews.add-review.age',
              defaultMessage: 'Age of child',
            })}
            name="age"
          >
            <option value="">
              {intl.formatMessage({
                id: 'widgets.reviews.add-review.age.choose',
                defaultMessage: 'Choose',
              })}
            </option>

            {ages.map((age) => (
              <option key={age} value={age}>
                {age}
              </option>
            ))}
          </FieldsSelect>
        )}

        <Hr className={classes.hr} />

        <Typography variant="h4">
          <FormattedMessage
            defaultMessage="Personal information"
            id="widgets.reviews.add-review.personal-info"
          />
        </Typography>

        <FieldsInput
          className={classes.fieldName}
          control={control}
          disabled={creating}
          label={intl.formatMessage({
            id: 'widgets.reviews.add-review.name',
            defaultMessage: 'Name (will be used at your review)',
          })}
          name="name"
          required
        />

        <FieldsInput
          control={control}
          disabled={creating}
          label={intl.formatMessage({
            id: 'widgets.reviews.add-review.email',
            defaultMessage: 'Email',
          })}
          name="email"
          hidden
        />

        <FieldsInput
          control={control}
          disabled={creating}
          label={intl.formatMessage({
            id: 'widgets.reviews.add-review.email',
            defaultMessage: 'Email',
          })}
          name="userEmail"
          validate={{
            ...validations.isEmail,
          }}
          required
        />

        <Hr className={classes.hr} />

        <FieldsCheckbox
          control={control}
          disabled={creating}
          label={intl.formatMessage({
            id: 'widgets.reviews.add-review.anonymously',
            defaultMessage: 'Create review anonymously.',
          })}
          name="submitAnonymous"
        />

        <FieldsCheckbox
          control={control}
          disabled={creating}
          label={intl.formatMessage(
            {
              id: 'widgets.reviews.add-review.terms',
              defaultMessage:
              'You agree with our \'<a\' href="{privacyUrl}" target="_blank">privacy policy\'</a>\', \'<a\' href="{dataCollectionUrl}" target="_blank">data security\'</a>\' and \'<a\' href="{termsUrl}" target="_blank">terms and conditions\'</a>\'.',
            },
            {
              privacyUrl: localeConfig.urls?.privacy,
              termsUrl: localeConfig.urls?.termsAndConditions,
              dataCollectionUrl: localeConfig.urls?.dataCollection
            },
          )}
          name="agreeTCAndPrivacyStatement"
          required
        />

        {config.contactEmail ? (
          <Typography variant="body">
            <FormattedHtmlMessage
              message={intl.formatMessage(
                {
                  id: 'widgets.reviews.add-review.revoke-by-email',
                  defaultMessage:
                    "You can revoke this agreement at any time by writing an e-mail to '<a' href=\"mailto:{contactEmail}\">{contactEmail}'</a>'.",
                },
                {
                  contactEmail: config.contactEmail,
                },
              )}
            />
          </Typography>
        ) : undefined}

        <Button
          className={classes.submit}
          data-testid="btn.reviews.submit"
          disabled={creating}
          type="submit"
          variant="brand"
        >
          <FormattedMessage
            defaultMessage="Submit review"
            id="btn.reviews.submit"
          />
        </Button>
      </form>
    </>
  )
}

export const useStyles = makeStyles(
  (theme: Theme) => ({
    backButton: {
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.primary.main,
      margin: theme.spacing(0, 0, 3),
      padding: 0,
      fontSize: 14,

      '& svg': {
        fill: 'currentColor',
        marginRight: theme.spacing(0.5),
      },

      '& > span': {
        display: 'flex',
        alignItems: 'center',
      },
    },
    root: {
      [theme.breakpoints.up('md')]: {
        border: '1px solid #DDDDDD',
        borderRadius: theme.shape.borderRadius(),
        padding: theme.spacing(4, 12, 7),
        marginBottom: theme.spacing(2),
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
      },
    },
    title: {
      marginBottom: 0,
    },
    subtitle: {},
    productWrap: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(3),
      backgroundColor: theme.utils.hexToRgba(theme.palette.primary.main, 0.1),
      borderRadius: theme.shape.borderRadius(),
      marginTop: theme.spacing(4),
      boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',

      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(5),
        padding: theme.spacing(3, 4),
      },
    },
    productImage: {
      marginRight: theme.spacing(2),

      '& img': {
        verticalAlign: 'top',
      },
    },
    productName: {
      color: theme.palette.primary.main,
      fontWeight: 500,
      margin: 0,
    },
    productLink: {
      display: 'block',
      margin: theme.spacing(0.5, 0, 0),
      fontSize: 14,
      textDecoration: 'none',

      '&:hover': {
        textDecoration: 'underline',
      },
    },
    commentField: {
      '& textarea': {
        minHeight: 200,
      },
    },
    hr: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),

      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(5),
      },
    },
    submit: {
      width: '100%',
      justifyContent: 'center',
      marginTop: theme.spacing(6),

      [theme.breakpoints.up('md')]: {
        width: 'auto',
      },
    },
    fieldName: {
      marginBottom: theme.spacing(2),
    },
  }),
  { name: 'CreateReview' },
)

export default CreateReview
